import { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import { format } from 'date-fns';
import * as locales from 'date-fns/locale';
import { useTranslation } from 'next-i18next';
import Tooltip from 'react-tooltip-lite';
import { themeColors } from 'utils/tailwind/tailwindHelpers';
import * as yup from 'yup';

import AddEditTeamMember from '@/modules/settings/Team/components/AddEditTeamMember';
import { ArchiveDeleteConfirmationModal } from '@/modules/settings/Team/components/ArchiveDeleteConfirmationModal';
import { ICompanyTeamsSettings } from '@/modules/settings/Team/types';
import { Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import DotedMenu from '@/shared/components/molecules/DotedMenu';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { roles } from '@/shared/constant/roles-and-permissions';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { validatePhoneNumber } from '@/shared/lib/libphonenumber';

import Edit from '~/images/shared/edit.svg';
import InfoCircle from '~/images/shared/ExleminationMark.svg';
import DotsMenu from '~/images/shared/HorizontalHollowDots.svg';
import Profile from '~/images/shared/Profile.svg';
import Trash from '~/images/shared/TrashWithoutColor.svg';

export const useCompanyTeamSettings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [isDemoFarm] = useStore((store) => store?.isDemoFarm);
  const { openModal, closeModal } = useModal();

  const getTeamMembersTable = () => {
    const columnHelper =
      createColumnHelper<ICompanyTeamsSettings.ICompanyUser>();
    return [
      columnHelper.accessor('firstName', {
        header: t('name'),
        cell: ({ getValue, row: { original } }) => (
          <div className='flex items-center gap-8'>
            <div className='flex h-40 w-40 items-center justify-center rounded-full bg-outline-default'>
              <Profile width='24' height='24' />
            </div>
            <div>
              <Typography weight='semiBold'>{`${getValue()} ${
                original.lastName
              }`}</Typography>
              <Typography
                variant='subtitle2'
                weight='semiBold'
                className='text-surface-10'
              >
                {original.email}
              </Typography>
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: t('status'),
        cell: ({ getValue }) => (
          <Typography
            variant='caption'
            weight='semiBold'
            className={classNames(
              ' flex h-24 w-56 items-center justify-center rounded-lg p-4',
              getValue() === 'ACTIVE' && 'bg-secondary-90 text-green-300',
              ['INACTIVE', 'INVITED'].includes(getValue()) &&
                'bg-neutralVariant-96 text-tertiary-20'
            )}
          >
            {t(getValue().toLowerCase())}
          </Typography>
        ),
      }),
      columnHelper.accessor('phoneNumber', {
        header: t('Mobile Number'),
        cell: ({ getValue }) => (
          <Typography className='text-surface-10'>{getValue()}</Typography>
        ),
      }),
      columnHelper.accessor('roleName', {
        header: t('Role'),
        cell: ({ getValue, row: { original } }) => (
          <div className='flex items-center gap-8'>
            <Typography className='text-surface-10'>{t(getValue())}</Typography>
            {original.roleDescription && (
              <Tooltip
                content={
                  <Typography
                    variant='caption'
                    weight='semiBold'
                    className='py-8 px-12'
                  >
                    {t(original.roleDescription)}
                  </Typography>
                }
                background={themeColors().neutralVariant[90]}
                className='react-tooltip-rounded'
                arrow
                arrowSize={10}
                direction='bottom'
                tagName='div'
                tipContentHover
                padding={0}
              >
                <span className='cursor-pointer text-primary-default'>
                  <InfoCircle width='16' height='16' />
                </span>
              </Tooltip>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('jobTitle', {
        header: t('Job Title'),
        cell: ({ getValue }) => (
          <Typography className='text-surface-10'>
            {getValue() || '-'}
          </Typography>
        ),
      }),
      columnHelper.accessor('assignedProperties', {
        header: t('Assigned Properties'),
        cell: ({ getValue }) =>
          getValue()?.length ? (
            <PartialListWithTooltip
              limit={3}
              items={getValue().map((property) => ({
                description: property.name,
              }))}
            />
          ) : (
            '-'
          ),
      }),
      columnHelper.accessor('createdAt', {
        header: t('date-added'),
        cell: ({ getValue }) => (
          <Typography className='text-surface-10'>
            {getValue()
              ? format(new Date(getValue()), 'd MMMM yyyy', {
                  locale: locales[language as keyof typeof locales],
                })
              : '-'}
          </Typography>
        ),
      }),
      columnHelper.accessor('createdByName', {
        header: t('added-by'),
        cell: ({ getValue }) => (
          <Typography className='text-surface-10'>
            {getValue() ?? '-'}
          </Typography>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({
          row: {
            original: {
              firstName,
              lastName,
              assignedProperties,
              email,
              jobTitle,
              roleId,
              phoneNumber,
              id,
              status,
              billingUser,
            },
          },
        }) => (
          <div className='flex w-fit items-center justify-around gap-24'>
            <div
              onClick={(e) => {
                e.stopPropagation();
                openModal(
                  <AddEditTeamMember
                    memberId={id}
                    isBillingUser={Boolean(billingUser)}
                    initialValues={{
                      firstName,
                      lastName,
                      phoneNumber,
                      roleId,
                      assignedProperties: assignedProperties
                        ? assignedProperties.map((property) => property.id)
                        : undefined,
                      email,
                      jobTitle: jobTitle ?? '',
                    }}
                    formName='settings edit team member'
                  />,
                  {
                    title: t('edit-team-member'),
                    variant: 'full',
                    onClose: () =>
                      openModal(
                        <ConfirmationModal
                          header={t('going-somewhere-else')}
                          message={t('you-will-lose-all-data')}
                          variant='warning'
                          dismissText={t('continue-editing')}
                          confirmText={t('discard-and-exit')}
                          onDismissRemoveNumber={1}
                          onConfirm={closeModal}
                          buttonsAlignment='col'
                          preferredAction='dismiss'
                        />
                      ),
                  }
                );
              }}
              className='font-semibold items-center justify-center gap-1'
            >
              <BaseIcon
                svgClasses='text-primary-default cursor-pointer'
                size='md'
              >
                <Edit />
              </BaseIcon>
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
              }}
              className='font-semibold cursor-pointer items-center justify-center gap-1'
            >
              {!billingUser ? (
                <DotedMenu
                  list={[
                    {
                      title: (
                        <div className='flex items-center gap-2 text-error-400'>
                          <Trash width='24' height='24' />
                          <span>
                            {t(status === 'INVITED' ? 'delete' : 'deactivate')}
                          </span>
                        </div>
                      ),
                      id: '0',
                      action: () =>
                        openModal(
                          isDemoFarm ? (
                            <DemoCTA />
                          ) : (
                            <ArchiveDeleteConfirmationModal
                              id={id}
                              type={status === 'INVITED' ? 'delete' : 'archive'}
                            />
                          )
                        ),
                    },
                  ]}
                  getTitle={(item) => item?.title}
                  getId={(item) => item?.id}
                  getAction={(item) => item.action}
                  onOpen={() => {
                    //
                  }}
                  DotsMenuIcon={<DotsMenu className='text-24' />}
                />
              ) : (
                <TippyTooltip content={t('billing-user-tooltip')}>
                  <DotsMenu className='cursor-not-allowed text-24 opacity-3' />
                </TippyTooltip>
              )}
            </div>
          </div>
        ),
      }),
    ];
  };

  const AddTeamMemberSchema: yup.SchemaOf<ICompanyTeamsSettings.IAddTeamMemberFormValues> =
    yup.object().shape({
      firstName: yup
        .string()
        .required(t('this-field-is-required'))
        .max(
          50,
          t('this-field-not-accept-more-than-x-character', { count: 50 })
        ),
      lastName: yup
        .string()
        .required(t('this-field-is-required'))
        .max(
          50,
          t('this-field-not-accept-more-than-x-character', { count: 50 })
        ),
      roleId: yup.number().required(t('this-field-is-required')),
      jobTitle: yup
        .string()
        .notRequired()
        .max(
          50,
          t('this-field-not-accept-more-than-x-character', { count: 50 })
        ),
      email: yup.string().email(t('Please enter a valid email')).notRequired(),
      // Required only if selected role is inventory specialist
      assignedProperties: yup
        .array()
        .of(yup.number())
        .test(
          'assignedProperties',
          t('this-field-is-required'),
          function (selectedRoles) {
            if (this.parent.roleId === roles.INVENTORY_SPECIALIST) {
              return !!(selectedRoles && selectedRoles.length);
            }
            return true;
          }
        )
        .optional(),
      invitationNote: yup.string().notRequired(),
      countryCode: yup.string().notRequired(),
      phoneNumber: yup
        .string()
        .nullable()
        .test('isValidPhoneNumber', t('wrong-phone-number'), (value) =>
          validatePhoneNumber(value, true)
        )
        .required(t('this-field-is-required')),
    });

  const checkAccessLoss = (oldRoleId: number, newRoleId: number) => {
    // Extract the union type of role values
    type RoleId = typeof roles[keyof typeof roles];

    const fieldAppRoles = new Set<RoleId>([
      roles.PRODUCTION_MANAGER,
      roles.COMPANY_ADMIN,
      roles.ONBOARDING_SUPERVISOR,
      roles.ONBOARDING,
      roles.SUPERVISOR,
      roles.SUPER_ADMIN,
    ]);

    const managerAppRoles = new Set<RoleId>([
      roles.OWNER,
      roles.SUPER_ADMIN,
      roles.FINANCIAL_MANAGER,
      roles.COMPANY_ADMIN,
      roles.PRODUCTION_MANAGER,
    ]);

    const aydiEyeRoles = new Set<RoleId>([
      roles.OWNER,
      roles.SUPER_ADMIN,
      roles.FINANCIAL_MANAGER,
      roles.INVENTORY_SPECIALIST,
      roles.PRINTHOUSE_SPECIALIST,
      roles.COMPANY_ADMIN,
      roles.PRODUCTION_MANAGER,
    ]);

    const hadFieldAppAccess = fieldAppRoles.has(oldRoleId as RoleId);
    const hasFieldAppAccess = fieldAppRoles.has(newRoleId as RoleId);
    const isLossingFieldAppAccess = hadFieldAppAccess && !hasFieldAppAccess;

    const hadManagerAppAccess = managerAppRoles.has(oldRoleId as RoleId);
    const hasManagerAppAccess = managerAppRoles.has(newRoleId as RoleId);
    const isLossingManagerAppAccess =
      hadManagerAppAccess && !hasManagerAppAccess;

    const hadAydiEyeAccess = aydiEyeRoles.has(oldRoleId as RoleId);
    const hasAydiEyeAccess = aydiEyeRoles.has(newRoleId as RoleId);
    const isLossingAydiEyeAccess = hadAydiEyeAccess && !hasAydiEyeAccess;

    return {
      isLossingFieldAppAccess,
      isLossingManagerAppAccess,
      isLossingAydiEyeAccess,
    };
  };

  return { getTeamMembersTable, AddTeamMemberSchema, checkAccessLoss };
};
